import type { ColumnDef, Row } from '@tanstack/vue-table'
import { Button } from '~/components/ui/button'
import AlphabeticSortIcon from '~/components/AlphabeticSortIcon.vue'
import BarsArrowUp from '~/components/BarsArrowUp.vue'
import EnvelopeIcon from '~/components/EnvelopeIcon.vue'

export const createColumns = (
  companyId: number | string,
  queryParams: ProgressTrackerFilterParams | null = null,
  canEdit?: boolean,
): ColumnDef<RejectedSubmission>[] => {
  const columns: ColumnDef<RejectedSubmission>[] = [
    {
      accessorKey: 'data_owner',
      accessorFn: row => `${row.data_owner?.first_name} ${row.data_owner?.last_name ?? ''}`,
      id: 'data_owner.name',
      header: ({ column }) => {
      // 'asc', 'desc', or undefined
        const isSorted = column.getIsSorted()
        const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 rotate-180' : ''

        return h('div', { class: 'flex justify-between items-center w-full' }, [
          h('span', { class: 'text-sm text-slate-800 font-semibold' }, 'Data Owner'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(isSorted === 'asc', true),
          }, () => h(AlphabeticSortIcon, { customStyle: `size-5 ${sortIconStyling}` })),
        ])
      },
      cell: ({ row, column }) => h('div', { class: 'capitalize text-left text-md text-foreground no-localize-translate' }, row.getValue(column.id)),
    },
    {
      accessorKey: 'submission_id',
      id: 'submission_id',
      header: () => h('span', { class: 'text-sm text-slate-800 font-semibold' }, 'Submission ID'),
      cell: ({ row, column }) => {
        return h(Button, {
          class: 'underline text-foreground text-md',
          variant: 'link',
          onClick: () => navigateTo(generateSubmissionPath(row)),
        }, () => [row.getValue(column.id)])
      },
    },
    {
      accessorKey: 'rejected_approvers',
      id: 'rejected_by',
      header: () => h('span', { class: 'text-sm text-slate-800 font-semibold' }, 'Rejected By'),
      cell: ({ row, column }) => {
        const rejected_approvers = row.getValue(column.id) as { id: number, first_name: string, last_name: string, email: string }[]
        const formatted = rejected_approvers.map(({ first_name, last_name }) => `${first_name} ${last_name ?? ''}`).join(', ')
        return h('div', { class: 'capitalize text-left text-md text-foreground no-localize-translate' }, formatted)
      },
    },
    {
      accessorKey: 'submission_date',
      id: 'submission_date',
      header: ({ column }) => {
      // 'asc', 'desc', or undefined
        const isSorted = column.getIsSorted()
        const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 !rotate-0' : ''

        return h('div', { class: 'flex justify-between items-center w-full' }, [
          h('span', { class: 'text-sm text-slate-800 font-semibold' }, 'Submission Date'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(column.getIsSorted() === 'asc', true),
          }, () => h(BarsArrowUp, { customStyle: `size-5 rotate-180 ${sortIconStyling}` })),
        ])
      },
      cell: ({ row, column }) => {
        const { $moment } = useNuxtApp()
        const formatted = $moment(row.getValue(column.id)).format('DD/MM/yyyy')
        return h('div', { class: 'capitalize text-left text-md text-foreground' }, formatted)
      },
    },
  ]

  if (canEdit !== false) {
    columns.push({
      id: 'actions',
      header: () => h('span', { class: 'max-w-min text-sm text-slate-800 font-semibold' }, 'Actions'),
      cell: ({ row }) => {
        return h(Button, {
          class: 'text-main-primary',
          variant: 'link',
          onClick: () => sendIndividualEmail(row, companyId, queryParams),
        }, () => h(EnvelopeIcon))
      },
    })
  }

  return columns
}

const generateSubmissionPath = (row: Row<RejectedSubmission>) => {
  const { $buffer } = useNuxtApp()

  const encodedCategory = $buffer.from(row.original.category.toString(), 'utf-8').toString('base64')
  const encodedId = $buffer.from(getSubmissionId(row).toString(), 'utf-8').toString('base64')

  return `/dashboard/data-upload/${encodedCategory}?submission=${encodedId}`
}

const getSubmissionId = (row: Row<RejectedSubmission>) => {
  return parseInt(row.original.submission_id.replace('SC-', ''))
}

const sendIndividualEmail = (row: Row<RejectedSubmission>, companyId: number | string, queryParams: ProgressTrackerFilterParams | null) => {
  const { protocol, host } = window.location
  const { $apis, $toast } = useNuxtApp()
  const submission_id = getSubmissionId(row)
  const submission_link = `${protocol}//${host}${generateSubmissionPath(row)}`

  const params: TrackerEmailReminderParams = {
    action: 'rejected_submissions',
    recipient_id: row.original.data_owner.id,
    submission_id,
    submission_link,
    redirect_link: `${protocol}//${host}/dashboard/data-upload/submissions`,
    ...(queryParams && { start_date: queryParams.start_date, end_date: queryParams.end_date }),
  }

  $apis.progressTracker
    .sendEmailReminder(companyId, params)
    .then(() => $toast.success(`Processing an email notification to ${row.original.data_owner.first_name}`))
}
